import { useCartModal } from "@/context/CartModalContext";
import { useCart } from "@/hooks/useCart";
import { useEffect, useRef, useState } from "react";
import useProductData from "@/hooks/useProductData";
import styles from "./CartModal.module.scss";

// Типізація для елементів кошика
interface CartItemType {
  goid: string;
  quantity: number;
  typeorder: "sellpart" | "exchpart";
}

// Тип для даних продукту
interface ProductData {
  pricevat?: number;
  priceexchvat?: number;
}

// Компонент для відображення загальної суми
const CartTotal = ({ cart }: { cart: CartItemType[] }) => {
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [productsData, setProductsData] = useState<Record<string, ProductData>>(
    {}
  );

  // Завантажуємо дані всіх продуктів
  useEffect(() => {
    const fetchProductsData = async () => {
      setLoading(true);
      const data: Record<string, ProductData> = {};

      const promises = cart.map(async (item) => {
        try {
          const response = await fetch(
            `https://crm.hixa.eu/external/goodsapi/?legid=1&type=good&goid=${item.goid}`,
            {
              method: "GET",
              headers: {
                Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
                Accept: "application/json",
                "User-Agent": "Mozilla/5.0 (compatible; SitemapGenerator/1.0)",
              },
            }
          );

          if (response.ok) {
            const apiData = await response.json();
            if (apiData.data && apiData.data.length > 0) {
              const productData = apiData.data[0];
              data[item.goid] = {
                pricevat: parseFloat(productData.pricevat),
                priceexchvat: parseFloat(productData.priceexchvat),
              };
            }
          }
        } catch (error) {
          console.error(`Error fetching product ${item.goid}:`, error);
        }
      });

      await Promise.all(promises);
      setProductsData(data);
      setLoading(false);
    };

    if (cart.length > 0) {
      fetchProductsData();
    } else {
      setProductsData({});
      setLoading(false);
      setTotalAmount(0);
    }
  }, [cart]);

  // Розраховуємо загальну суму
  useEffect(() => {
    if (!loading) {
      const total = cart.reduce((sum, item) => {
        const productData = productsData[item.goid];
        if (!productData) return sum;

        const price =
          item.typeorder === "sellpart"
            ? productData.pricevat
            : productData.priceexchvat;

        return sum + (price || 0) * item.quantity;
      }, 0);

      setTotalAmount(total);
    }
  }, [productsData, cart, loading]);

  if (loading && cart.length > 0) {
    return (
      <div className={styles.footer}>
        <p>Loading total...</p>
      </div>
    );
  }

  return (
    <div className={styles.footer}>
      <div className={styles.footerLine}>
        <div className={styles.total}>
          <p>Zwischensumme:</p>
          <p className={styles.totalPrice}>{totalAmount.toFixed(2)} €</p>
        </div>
      </div>
      <button
        className={styles.checkoutBtn}
        onClick={() => (window.location.href = "/checkout")}
      >
        Kasse
      </button>
    </div>
  );
};

// Оновлений компонент CartItem
const CartItem = ({
  item,
  removeFromCart,
}: {
  item: CartItemType;
  removeFromCart: (goid: string, typeorder: "sellpart" | "exchpart") => void;
}) => {
  const { product, loading } = useProductData(item.goid);

  if (loading) {
    return (
      <div className={styles.item}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={styles.item}>
        <p>Product not found</p>
      </div>
    );
  }

  // Перевірка на наявність ціни
  const price =
    item.typeorder === "sellpart" ? product.pricevat : product.priceexchvat;

  const validPrice = price ?? 0;

  return (
    <div className={styles.item} key={`${item.goid}-${item.typeorder}`}>
      <img
        src={product.imageUrl || "/images/Shop/template.webp"}
        alt={product.name}
      />
      <div className={styles.info}>
        <p className={styles.name}>{product.name}</p>
        <div className={styles.priceBlock}>
          <p>
            {item.quantity} × {validPrice.toFixed(2)} €
          </p>
          <p className={styles.typeorder}>{item.typeorder}</p>
        </div>

        <button
          className={styles.deleteBtn}
          onClick={() => removeFromCart(item.goid, item.typeorder)}
          aria-label="Artikel aus dem Warenkorb entfernen"
        >
          ×
        </button>
      </div>
    </div>
  );
};

const CartModal = () => {
  const { isOpen, closeCart } = useCartModal();
  const { cart, removeFromCart } = useCart();
  const modalRef = useRef<HTMLDivElement>(null);

  // Закриття по Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCart();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeCart]);

  // Закриття модального вікна при кліку поза ним
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        closeCart();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeCart]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} ref={modalRef}>
        <p className={styles.title}>Mein Warenkorb</p>
        <button className={styles.closeBtn} onClick={closeCart}>
          <img src="/images/close.svg" alt="Close button" />
        </button>

        {cart.length === 0 ? (
          <p className={styles.titleInfo}>
            Es befinden sich keine Produkte im Warenkorb.
          </p>
        ) : (
          <>
            <div className={styles.items}>
              {cart.map((item) => (
                <CartItem
                  key={`${item.goid}-${item.typeorder}`}
                  item={item}
                  removeFromCart={removeFromCart}
                />
              ))}
            </div>
            <CartTotal cart={cart} />
          </>
        )}
      </div>
    </div>
  );
};

export default CartModal;
